import { Component, OnInit } from '@angular/core';
import { WorkshopModel, WorkshopQuantityRequest } from '../../../models/workshop-model/workshop-model';
import { WorkshopsService } from '../../../services/workshops/workshops.service';

@Component({
  selector: 'app-workshops',
  templateUrl: './workshops.component.html',
  styleUrls: ['./workshops.component.css']
})
export class WorkshopsComponent implements OnInit {
  workshops: WorkshopModel[] = [];
  filteredWorkshops: WorkshopModel[] = [];
  loading = false;
  error = '';
  searchTerm = '';
  
  // Modal states
  showAddModal = false;
  showEditModal = false;
  showDeleteModal = false;
  showLabelsModal = false;
  showMultipleLabelsModal = false;
  
  // Multiple selection
  multipleSelectionMode = false;
  selectedWorkshops: WorkshopModel[] = [];
  
  // Form data
  newWorkshop: WorkshopModel = {
    name: '',
    address: '',
    phoneNumber: '',
    department: '',
    city: '',
    company: 'AUTOCOMERCIAL',
    alertOrder: false
  };
  
  editWorkshop: WorkshopModel = {
    name: '',
    address: '',
    phoneNumber: '',
    department: '',
    city: '',
    company: 'AUTOCOMERCIAL',
    alertOrder: false
  };
  
  selectedWorkshop: WorkshopModel | null = null;
  
  // Labels
  labelQuantity = 1;
  multipleLabelQuantity = 1;
  workshopQuantities: Map<number, number> = new Map();
  
  constructor(private workshopsService: WorkshopsService) {}

  ngOnInit(): void {
    this.loadWorkshops();
  }

  loadWorkshops(): void {
    this.loading = true;
    this.error = '';
    
    this.workshopsService.getAllWorkshops().subscribe({
      next: (workshops) => {
        this.workshops = workshops;
        this.filteredWorkshops = workshops;
        this.loading = false;
      },
      error: (error) => {
        this.error = 'Error al cargar talleres';
        this.loading = false;
        console.error('Error:', error);
      }
    });
  }

  onSearch(): void {
    if (!this.searchTerm.trim()) {
      this.filteredWorkshops = this.workshops;
      return;
    }
    
    const term = this.searchTerm.toLowerCase();
    this.filteredWorkshops = this.workshops.filter(workshop =>
      workshop.name.toLowerCase().includes(term) ||
      (workshop.address && workshop.address.toLowerCase().includes(term)) ||
      (workshop.phoneNumber && workshop.phoneNumber.includes(term)) ||
      (workshop.department && workshop.department.toLowerCase().includes(term)) ||
      (workshop.city && workshop.city.toLowerCase().includes(term)) ||
      workshop.company.toLowerCase().includes(term)
    );
  }

  // Add Workshop Modal
  openAddModal(): void {
    this.newWorkshop = {
      name: '',
      address: '',
      phoneNumber: '',
      department: '',
      city: '',
      company: 'AUTOCOMERCIAL',
      alertOrder: false
    };
    this.showAddModal = true;
  }

  closeAddModal(): void {
    this.showAddModal = false;
  }

  saveWorkshop(): void {
    if (!this.newWorkshop.name.trim()) {
      alert('El nombre del taller es requerido');
      return;
    }

    this.loading = true;
    this.workshopsService.createWorkshop(this.newWorkshop).subscribe({
      next: (workshop) => {
        this.workshops.push(workshop);
        this.onSearch(); // Refresh filtered list
        this.closeAddModal();
        this.loading = false;
        alert('Taller creado exitosamente');
      },
      error: (error) => {
        this.loading = false;
        alert('Error al crear taller: ' + (error.error || error.message));
        console.error('Error:', error);
      }
    });
  }

  // Edit Workshop Modal
  openEditModal(workshop: WorkshopModel): void {
    this.selectedWorkshop = workshop;
    this.editWorkshop = {
      name: workshop.name,
      address: workshop.address || '',
      phoneNumber: workshop.phoneNumber || '',
      department: workshop.department || '',
      city: workshop.city || '',
      company: workshop.company,
      alertOrder: workshop.alertOrder || false
    };
    this.showEditModal = true;
  }

  closeEditModal(): void {
    this.showEditModal = false;
    this.selectedWorkshop = null;
  }

  updateWorkshop(): void {
    if (!this.selectedWorkshop || !this.editWorkshop.name.trim()) {
      alert('El nombre del taller es requerido');
      return;
    }

    this.loading = true;
    this.workshopsService.updateWorkshop(this.selectedWorkshop.workshopId!, this.editWorkshop).subscribe({
      next: (updatedWorkshop) => {
        const index = this.workshops.findIndex(w => w.workshopId === updatedWorkshop.workshopId);
        if (index !== -1) {
          this.workshops[index] = updatedWorkshop;
          this.onSearch(); // Refresh filtered list
        }
        this.closeEditModal();
        this.loading = false;
        alert('Taller actualizado exitosamente');
      },
      error: (error) => {
        this.loading = false;
        alert('Error al actualizar taller: ' + (error.error || error.message));
        console.error('Error:', error);
      }
    });
  }

  // Delete Workshop Modal
  openDeleteModal(workshop: WorkshopModel): void {
    this.selectedWorkshop = workshop;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.selectedWorkshop = null;
  }

  deleteWorkshop(): void {
    if (!this.selectedWorkshop) return;

    this.loading = true;
    this.workshopsService.deleteWorkshop(this.selectedWorkshop.workshopId!).subscribe({
      next: () => {
        this.workshops = this.workshops.filter(w => w.workshopId !== this.selectedWorkshop!.workshopId);
        this.onSearch(); // Refresh filtered list
        this.closeDeleteModal();
        this.loading = false;
        alert('Taller eliminado exitosamente');
      },
      error: (error) => {
        this.loading = false;
        alert('Error al eliminar taller: ' + (error.error || error.message));
        console.error('Error:', error);
      }
    });
  }

  // Labels Modal
  openLabelsModal(workshop: WorkshopModel): void {
    this.selectedWorkshop = workshop;
    this.labelQuantity = 1;
    this.showLabelsModal = true;
  }

  closeLabelsModal(): void {
    this.showLabelsModal = false;
    this.selectedWorkshop = null;
  }

  generateLabels(): void {
    if (!this.selectedWorkshop || this.labelQuantity < 1 || this.labelQuantity > 100) {
      alert('La cantidad debe estar entre 1 y 100');
      return;
    }

    this.loading = true;
    this.workshopsService.downloadLabels(
      this.selectedWorkshop.workshopId!,
      { quantity: this.labelQuantity }
    ).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Etiquetas_${this.selectedWorkshop.name}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.loading = false;
        this.closeLabelsModal();
        alert('Etiquetas descargadas exitosamente');
      },
      error: (error) => {
        this.loading = false;
        alert('Error al descargar etiquetas: ' + (error.error || error.message));
      }
    });
  }

  // Multiple selection methods
  toggleMultipleSelection(): void {
    this.multipleSelectionMode = !this.multipleSelectionMode;
    if (!this.multipleSelectionMode) {
      this.selectedWorkshops = [];
    }
  }

  isWorkshopSelected(workshop: WorkshopModel): boolean {
    return this.selectedWorkshops.some(w => w.workshopId === workshop.workshopId);
  }

  toggleWorkshopSelection(workshop: WorkshopModel): void {
    const index = this.selectedWorkshops.findIndex(w => w.workshopId === workshop.workshopId);
    if (index >= 0) {
      this.selectedWorkshops.splice(index, 1);
    } else {
      this.selectedWorkshops.push(workshop);
    }
  }

  // Multiple labels modal methods
  openMultipleLabelsModal(): void {
    if (this.selectedWorkshops.length === 0) {
      alert('Debe seleccionar al menos un taller');
      return;
    }
    this.showMultipleLabelsModal = true;
  }

  closeMultipleLabelsModal(): void {
    this.showMultipleLabelsModal = false;
    this.multipleLabelQuantity = 1;
    this.workshopQuantities.clear();
  }

  updateWorkshopQuantity(workshopId: number, event: Event): void {
    const target = event.target as HTMLInputElement;
    const quantity = parseInt(target.value);
    
    if (quantity >= 1 && quantity <= 100) {
      this.workshopQuantities.set(workshopId, quantity);
    } else {
      // Si el valor no es válido, usar la cantidad por defecto
      this.workshopQuantities.delete(workshopId);
    }
  }

  generateMultipleLabels(): void {
    if (this.selectedWorkshops.length === 0) {
      alert('Debe seleccionar al menos un taller');
      return;
    }

    // Validar que todas las cantidades estén entre 1 y 100
    const invalidQuantities = this.selectedWorkshops.filter(workshop => {
      const quantity = this.workshopQuantities.get(workshop.workshopId!) || this.multipleLabelQuantity;
      return quantity < 1 || quantity > 100;
    });

    if (invalidQuantities.length > 0) {
      alert('La cantidad debe estar entre 1 y 100 para cada taller');
      return;
    }

    this.loading = true;
    
    // Crear array de WorkshopQuantityRequest
    const workshopQuantities: WorkshopQuantityRequest[] = this.selectedWorkshops.map(workshop => ({
      workshopId: workshop.workshopId!,
      quantity: this.workshopQuantities.get(workshop.workshopId!) || this.multipleLabelQuantity
    }));
    
    this.workshopsService.downloadMultipleLabels({
      workshopQuantities: workshopQuantities
    }).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Etiquetas_MultiplesTalleres_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.loading = false;
        this.closeMultipleLabelsModal();
        // Clear selection after successful generation
        this.selectedWorkshops = [];
        this.workshopQuantities.clear();
        alert('Etiquetas descargadas exitosamente');
      },
      error: (error) => {
        this.loading = false;
        alert('Error al descargar etiquetas: ' + (error.error || error.message));
      }
    });
  }

  trackByWorkshopId(index: number, workshop: WorkshopModel): number {
    return workshop.workshopId || 0;
  }
} 